/**
 * NetworkFailureModal
 *
 * Centered modal with backdrop for network-related interruptions.
 * Shared by:
 *  - SyncStatusIndicator: device-level offline state
 *  - Survey submission retry: transient submit failure
 *
 * Blocks underlying UI via fixed-position backdrop.
 */

import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { DialogActions, DialogFrame, DialogNotice } from '@/components/dialog';
import styles from './NetworkFailureModal.module.css';

export interface NetworkFailureModalProps {
  title: string;
  description?: ReactNode;
  subDescription?: ReactNode;
  retryLabel: string;
  retryingLabel: string;
  retryFailedMessage?: string;
  /**
   * Called when user clicks retry. Resolve `true` if the action succeeded
   * (modal should hide externally); resolve/return `false` to surface the
   * `retryFailedMessage` inline without hiding the modal.
   */
  onRetry: () => Promise<boolean> | boolean;
  ariaLabelledBy?: string;
}

/**
 * Render-prop variant handles its own retry-in-flight state so callers
 * only implement the attempt logic.
 */
export function NetworkFailureModal({
  title,
  description,
  subDescription,
  retryLabel,
  retryingLabel,
  retryFailedMessage,
  onRetry,
  ariaLabelledBy = 'network-failure-title',
}: NetworkFailureModalProps) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [showFailed, setShowFailed] = useState(false);

  const handleClick = useCallback(async () => {
    setShowFailed(false);
    setIsRetrying(true);
    try {
      const ok = await onRetry();
      if (!ok) setShowFailed(true);
    } catch {
      setShowFailed(true);
    } finally {
      setIsRetrying(false);
    }
  }, [onRetry]);

  return (
    <DialogFrame
      isOpen
      title={title}
      description={description}
      hideCloseButton
      closeOnOverlayClick={false}
      ariaLabelledBy={ariaLabelledBy}
      size="sm"
      footer={(
        <DialogActions
          confirmLabel={isRetrying ? retryingLabel : retryLabel}
          onConfirm={handleClick}
          confirmDisabled={isRetrying}
          confirmLoading={isRetrying}
        />
      )}
    >
      <div className={styles.icon} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 1l22 22" />
            <path d="M16.72 11.06A10.94 10.94 0 0119 12.55" />
            <path d="M5 12.55a10.94 10.94 0 015.17-2.39" />
            <path d="M10.71 5.05A16 16 0 0122.58 9" />
            <path d="M1.42 9a15.91 15.91 0 014.7-2.88" />
            <path d="M8.53 16.11a6 6 0 016.95 0" />
            <line x1="12" y1="20" x2="12.01" y2="20" />
          </svg>
      </div>
      {subDescription && <p className={styles.subDescription}>{subDescription}</p>}
      {showFailed && retryFailedMessage ? (
        <DialogNotice>
          <span role="alert">{retryFailedMessage}</span>
        </DialogNotice>
      ) : null}
    </DialogFrame>
  );
}

export default NetworkFailureModal;
